import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion, AnimatePresence } from "motion/react";
import { z } from "zod";
import styles from "./SearchModal.module.css";
import {
  ModalCloseIcon,
  SearchEmptyStateIcon,
  SchoolNotFoundIcon,
} from "@assets";
import { VirtualInfiniteScroll } from "@components/VirtualInfiniteScroll";
import {
  SchoolDetailsCard,
  SchoolDetailsCardSkeleton,
} from "@components/SchoolRegistration/Cards";
import {
  useDebouncedSchoolQuery,
  useResponsiveBreakpoints,
  useBodyScrollLock,
} from "@hooks";
import type { SchoolData } from "@mocks/schoolDetails";
import { useSchoolStore } from "@store";
import { pincodeSearchSchema } from "../../schema";

// Extract only the pincode and schoolName fields from the existing schema
const modalFormSchema = pincodeSearchSchema.pick({
  pincode: true,
  schoolName: true,
});

type ModalFormData = z.infer<typeof modalFormSchema>;

interface PincodeSearchModalProps {
  isOpen: boolean;
  title: string;
  pincode: string;
  state: string;
  district: string;
  city: string;
  schoolName: string;
  onPincodeChange: (value: string) => void;
  onSchoolNameChange: (value: string) => void;
  onClose: () => void;
  onSchoolSelect?: (school: SchoolData) => void;
}

const PincodeSearchModal: React.FC<PincodeSearchModalProps> = ({
  isOpen,
  title,
  pincode,
  state,
  district,
  city,
  schoolName,
  onPincodeChange,
  onSchoolNameChange,
  onClose,
  onSchoolSelect,
}) => {
  const { setSelectedSchool } = useSchoolStore();
  const { isMobile } = useResponsiveBreakpoints();

  // Lock body scroll when mobile modal is open
  useBodyScrollLock(isOpen && isMobile);

  // React Hook Form setup
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<ModalFormData>({
    defaultValues: {
      pincode: pincode || "",
      schoolName: schoolName || "",
    },
    mode: "onChange",
    resolver: zodResolver(modalFormSchema),
  });

  const formPincode = watch("pincode");
  const formSchoolName = watch("schoolName");

  // Check if pincode fields are valid - pincode validation + location data availability
  const isPincodeValid =
    !errors.pincode && formPincode?.length === 6 && state && district && city;
  const isSchoolNameEnabled = isPincodeValid;

  // School search query - only search when validation passes and school name has 3+ chars
  const isValidSearch =
    isSchoolNameEnabled && formSchoolName?.trim().length >= 3;
  const schoolQuery = useDebouncedSchoolQuery({
    searchTerm: formSchoolName || "",
    enabled: isOpen && Boolean(isValidSearch),
    limit: 10,
  });

  // Sync form values with parent props when modal opens
  useEffect(() => {
    if (isOpen) {
      setValue("pincode", pincode || "");
      setValue("schoolName", schoolName || "");
    }
  }, [isOpen, pincode, schoolName, setValue]);

  // Handle pincode change with validation
  const handlePincodeChange = (inputValue: string) => {
    setValue("pincode", inputValue);
    onPincodeChange(inputValue);
    // Trigger validation
    trigger("pincode");
  };

  // Handle school name change
  const handleSchoolNameChange = (inputValue: string) => {
    if (isSchoolNameEnabled) {
      setValue("schoolName", inputValue);
      onSchoolNameChange(inputValue);
      // Trigger validation
      trigger("schoolName");
    }
  };

  // Handle school selection
  const handleSchoolSelect = (school: SchoolData) => {
    setSelectedSchool(school);
    onSchoolSelect?.(school);
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // For mobile, render directly without portal and overlay
  if (isMobile) {
    if (!isOpen) return null;

    return (
      <AnimatePresence>
        <motion.div
          className={styles.mobileModal}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pincode-search-modal-title"
        >
          <div className={styles.titleRow}>
            <h2 id="pincode-search-modal-title" className={styles.title}>
              {title}
            </h2>
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close"
              onClick={onClose}
            >
              <img src={ModalCloseIcon} alt="Close" width={16} height={16} />
            </button>
          </div>
          <div className={styles.content}>
            {/* 2x2 Grid Layout - Pincode/State row, District/City row */}
            <div className={styles.pincodeFieldsSection}>
              {/* First Row: Pincode + State */}
              <div className={styles.firstFieldsRow}>
                {/* Pincode Field */}
                <div className={styles.halfField}>
                  <div
                    className={`${styles.mobileFieldContainer} ${styles.pincodeField}`}
                  >
                    <div className={styles.floatingLabel}>Pincode</div>
                    <Controller
                      name="pincode"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="pincode-modal-input"
                          className={styles.mobileFieldInput}
                          onChange={(e) => {
                            field.onChange(e);
                            handlePincodeChange(e.target.value);
                          }}
                          autoFocus
                        />
                      )}
                    />
                  </div>
                  {errors.pincode && (
                    <motion.p
                      className={styles.errorMessage}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.pincode.message}
                    </motion.p>
                  )}
                </div>

                {/* State Field */}
                <div className={styles.halfField}>
                  <div
                    className={`${styles.mobileFieldContainer} ${styles.readOnly}`}
                  >
                    <div className={styles.floatingLabel}>State</div>
                    <span className={styles.mobileFieldText}>
                      {state || "Karnataka"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Second Row: District + City */}
              <div className={styles.secondFieldsRow}>
                {/* District Field */}
                <div className={styles.halfField}>
                  <div
                    className={`${styles.mobileFieldContainer} ${styles.readOnly}`}
                  >
                    <div className={styles.floatingLabel}>District</div>
                    <span className={styles.mobileFieldText}>
                      {district || "Bangalore Urban"}
                    </span>
                  </div>
                </div>

                {/* City Field */}
                <div className={styles.halfField}>
                  <div
                    className={`${styles.mobileFieldContainer} ${styles.readOnly}`}
                  >
                    <div className={styles.floatingLabel}>City</div>
                    <span className={styles.mobileFieldText}>
                      {city || "Bangalore"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* School Name Field */}
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="school-name-modal-input">
                School Name
              </label>
              <div
                className={`${styles.inputWrapper} ${
                  !isSchoolNameEnabled ? styles.disabled : ""
                }`}
              >
                <input
                  id="school-name-modal-input"
                  className={`${styles.input} ${
                    !isSchoolNameEnabled ? styles.disabled : ""
                  }`}
                  placeholder={
                    isSchoolNameEnabled
                      ? "Start typing school name"
                      : "Enter valid pincode first"
                  }
                  value={formSchoolName || ""}
                  onChange={(e) => handleSchoolNameChange(e.target.value)}
                  disabled={!isSchoolNameEnabled}
                />
              </div>
            </div>

            <div className={styles.dynamicContent}>
              {!isSchoolNameEnabled ? (
                <div className={styles.emptyState}>
                  <img
                    src={SearchEmptyStateIcon}
                    alt="Search empty"
                    width={48}
                    height={48}
                  />
                  <div className={styles.emptyText}>
                    Enter valid pincode to search schools
                  </div>
                  <div className={styles.emptySubtext}>
                    Complete the pincode fields to enable school search
                  </div>
                </div>
              ) : (formSchoolName || "").trim().length < 3 ? (
                <div className={styles.emptyState}>
                  <img
                    src={SearchEmptyStateIcon}
                    alt="Search empty"
                    width={48}
                    height={48}
                  />
                  <div className={styles.emptyText}>
                    Start typing to search for schools
                  </div>
                  <div className={styles.emptySubtext}>
                    Enter at least 3 characters to begin searching
                  </div>
                </div>
              ) : schoolQuery.isEmpty ? (
                <div className={styles.noDataState}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>
                      <img
                        src={SchoolNotFoundIcon}
                        alt="School not found"
                        width={54}
                        height={54}
                      />
                    </div>
                    <div className={styles.noDataTextContainer}>
                      <div className={styles.noDataTitle}>
                        Didn't find your school?
                      </div>
                      <div className={styles.noDataDescription}>
                        Don't worry! If your school isn't in our database yet,
                        you can add it yourself or get help from our support
                        team.
                      </div>
                    </div>
                    <div className={styles.noDataActions}>
                      <button className={styles.primaryAction}>
                        Register new school
                      </button>
                      <button className={styles.secondaryAction}>
                        Contact support
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <VirtualInfiniteScroll
                  items={schoolQuery.schools}
                  isLoading={schoolQuery.isLoading}
                  hasNextPage={schoolQuery.hasNextPage}
                  loadMore={schoolQuery.fetchNextPage}
                  direction="vertical"
                  itemsPerRow={isMobile ? 1 : 2}
                  estimatedItemSize={300}
                  height={490}
                  width="100%"
                  overscan={3}
                  enableKeyboardNavigation={true}
                  skeletonCount={6}
                  renderItem={(school: unknown) => {
                    const schoolData = school as SchoolData;
                    return (
                      <SchoolDetailsCard
                        key={schoolData.id}
                        schoolName={schoolData.schoolName}
                        udiseCode={schoolData.udiseCode}
                        govtSchoolId={schoolData.govtSchoolId}
                        state={schoolData.state}
                        district={schoolData.district}
                        city={schoolData.city}
                        pincode={schoolData.pincode}
                        address={schoolData.address}
                        status={schoolData.status}
                      />
                    );
                  }}
                  renderSkeleton={() => <SchoolDetailsCardSkeleton />}
                  onItemSelect={(item: unknown) => {
                    handleSchoolSelect(item as SchoolData);
                  }}
                  aria-label="Search results for school search"
                  aria-describedby="school-search-results-description"
                />
              )}

              {/* Screen reader description */}
              <div
                id="school-search-results-description"
                className={styles.srOnly}
              >
                {schoolQuery.schools.length > 0
                  ? `Found ${schoolQuery.totalCount} schools. Use arrow keys to navigate, Enter to select.`
                  : "Search results will appear here"}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Desktop version with portal and overlay
  const overlayRoot = document.getElementById("overlay-root");
  if (!overlayRoot || !isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        className={styles.overlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className={styles.modal}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 10, opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pincode-search-modal-title"
        >
          <div className={styles.titleRow}>
            <h2 id="pincode-search-modal-title" className={styles.title}>
              {title}
            </h2>
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close"
              onClick={onClose}
            >
              <img src={ModalCloseIcon} alt="Close" width={16} height={16} />
            </button>
          </div>
          <div className={styles.content}>
            {/* All 4 fields in one row - Pincode, State, District, City */}
            <div className={styles.pincodeFieldsSection}>
              <div className={styles.allFieldsRow}>
                {/* Pincode Field - 1/4 width */}
                <div className={styles.quarterField}>
                  <div
                    className={`${styles.inputWrapper} ${styles.pincodeInputWrapper}`}
                  >
                    <Controller
                      name="pincode"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="pincode-modal-input-desktop"
                          className={`${styles.input} ${
                            errors.pincode ? styles.inputError : ""
                          }`}
                          onChange={(e) => {
                            field.onChange(e);
                            handlePincodeChange(e.target.value);
                          }}
                          autoFocus
                        />
                      )}
                    />
                  </div>
                  {errors.pincode && (
                    <motion.p
                      className={styles.errorMessage}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.pincode.message}
                    </motion.p>
                  )}
                </div>

                {/* State Field - 1/4 width */}
                <div className={styles.quarterField}>
                  <div
                    className={`${styles.inputWrapper} ${styles.readOnly} ${
                      state ? styles.populated : ""
                    }`}
                  >
                    <span className={styles.readOnlyText}>
                      {state || "Karnataka"}
                    </span>
                  </div>
                </div>

                {/* District Field - 1/4 width */}
                <div className={styles.quarterField}>
                  <div
                    className={`${styles.inputWrapper} ${styles.readOnly} ${
                      district ? styles.populated : ""
                    }`}
                  >
                    <span className={styles.readOnlyText}>
                      {district || "Bangalore Urban"}
                    </span>
                  </div>
                </div>

                {/* City Field - 1/4 width */}
                <div className={styles.quarterField}>
                  <div
                    className={`${styles.inputWrapper} ${styles.readOnly} ${
                      city ? styles.populated : ""
                    }`}
                  >
                    <span className={styles.readOnlyText}>
                      {city || "Bangalore"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* School Name Field */}
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="school-name-modal-input">
                School Name
              </label>
              <div
                className={`${styles.inputWrapper} ${
                  !isSchoolNameEnabled ? styles.disabled : ""
                }`}
              >
                <input
                  id="school-name-modal-input"
                  className={`${styles.input} ${
                    !isSchoolNameEnabled ? styles.disabled : ""
                  }`}
                  placeholder={
                    isSchoolNameEnabled
                      ? "Start typing school name"
                      : "Enter valid pincode first"
                  }
                  value={formSchoolName || ""}
                  onChange={(e) => handleSchoolNameChange(e.target.value)}
                  disabled={!isSchoolNameEnabled}
                />
              </div>
            </div>

            <div className={styles.dynamicContent}>
              {!isSchoolNameEnabled ? (
                <div className={styles.emptyState}>
                  <img
                    src={SearchEmptyStateIcon}
                    alt="Search empty"
                    width={48}
                    height={48}
                  />
                  <div className={styles.emptyText}>
                    Enter valid pincode to search schools
                  </div>
                  <div className={styles.emptySubtext}>
                    Complete the pincode fields to enable school search
                  </div>
                </div>
              ) : (formSchoolName || "").trim().length < 3 ? (
                <div className={styles.emptyState}>
                  <img
                    src={SearchEmptyStateIcon}
                    alt="Search empty"
                    width={48}
                    height={48}
                  />
                  <div className={styles.emptyText}>
                    Start typing to search for schools
                  </div>
                  <div className={styles.emptySubtext}>
                    Enter at least 3 characters to begin searching
                  </div>
                </div>
              ) : schoolQuery.isEmpty ? (
                <div className={styles.noDataState}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>
                      <img
                        src={SchoolNotFoundIcon}
                        alt="School not found"
                        width={54}
                        height={54}
                      />
                    </div>
                    <div className={styles.noDataTextContainer}>
                      <div className={styles.noDataTitle}>
                        Didn't find your school?
                      </div>
                      <div className={styles.noDataDescription}>
                        Don't worry! If your school isn't in our database yet,
                        you can add it yourself or get help from our support
                        team.
                      </div>
                    </div>
                    <div className={styles.noDataActions}>
                      <button className={styles.primaryAction}>
                        Register new school
                      </button>
                      <button className={styles.secondaryAction}>
                        Contact support
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <VirtualInfiniteScroll
                  items={schoolQuery.schools}
                  isLoading={schoolQuery.isLoading}
                  hasNextPage={schoolQuery.hasNextPage}
                  loadMore={schoolQuery.fetchNextPage}
                  direction="vertical"
                  itemsPerRow={isMobile ? 1 : 2}
                  estimatedItemSize={240}
                  height={490}
                  width="100%"
                  overscan={3}
                  enableKeyboardNavigation={true}
                  skeletonCount={6}
                  renderItem={(school: unknown) => {
                    const schoolData = school as SchoolData;
                    return (
                      <SchoolDetailsCard
                        key={schoolData.id}
                        schoolName={schoolData.schoolName}
                        udiseCode={schoolData.udiseCode}
                        govtSchoolId={schoolData.govtSchoolId}
                        state={schoolData.state}
                        district={schoolData.district}
                        city={schoolData.city}
                        pincode={schoolData.pincode}
                        address={schoolData.address}
                        status={schoolData.status}
                      />
                    );
                  }}
                  renderSkeleton={() => <SchoolDetailsCardSkeleton />}
                  onItemSelect={(item: unknown) => {
                    handleSchoolSelect(item as SchoolData);
                  }}
                  aria-label="Search results for school search"
                  aria-describedby="school-search-results-description"
                />
              )}

              {/* Screen reader description */}
              <div
                id="school-search-results-description"
                className={styles.srOnly}
              >
                {schoolQuery.schools.length > 0
                  ? `Found ${schoolQuery.totalCount} schools. Use arrow keys to navigate, Enter to select.`
                  : "Search results will appear here"}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    overlayRoot
  );
};

export default PincodeSearchModal;
